// Check if LogoMarqueeSection is already defined to prevent redeclaration
if (!window.LogoMarqueeSection) {
  class LogoMarqueeSection extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.track = this.querySelector('.logo-marquee__track');
      this.setupScrollSpeed();
      this.setupPauseOnHover();
      this.adjustLogoSpacing();
      this.startAnimationAfterLoad();

      // Recalculate spacing on resize with debounce
      this._resizeTimeout = null;
      this._onResize = () => {
        clearTimeout(this._resizeTimeout);
        this._resizeTimeout = setTimeout(() => this.adjustLogoSpacing(), 150);
      };
      window.addEventListener('resize', this._onResize);
    }

    startAnimationAfterLoad() {
      // Wait for images to load, then start animation
      const images = this.querySelectorAll('img');
      
      if (images.length === 0) {
        // No images, start animation immediately
        this.startAnimation();
        return;
      }
      
      let loadedImages = 0;
      const totalImages = images.length;
      
      const checkAllLoaded = () => {
        loadedImages++;
        if (loadedImages >= totalImages) {
          setTimeout(() => this.startAnimation(), 500); // Increased delay for layout stability
        }
      };
      
      images.forEach(img => {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.addEventListener('load', checkAllLoaded);
          img.addEventListener('error', checkAllLoaded); // Handle failed loads
        }
      });
      
      // Fallback: start animation after 2 seconds regardless
      setTimeout(() => {
        if (!this.track?.classList.contains('logo-marquee__track--animated')) {
          this.startAnimation();
          // ensure spacing is recalculated after images have had a chance to layout
          this.adjustLogoSpacing();
        }
      }, 2000);
    }
    
    startAnimation() {
      if (this.track) {
        this.track.classList.add('logo-marquee__track--animated');
      }
    }

    adjustLogoSpacing() {
      const sets = this.querySelectorAll('.logo-marquee__set');
      if (sets.length === 0) return;

      // Use a responsive gap (15vw) to ensure consistency on big screens
      const fixedGap = window.innerWidth * 0.15;

      sets.forEach(set => {
        set.style.gap = `${fixedGap}px`;
        set.style.justifyContent = 'flex-start';
        set.style.paddingRight = `${fixedGap}px`;
        set.style.minWidth = '0'; // Allow sets to be smaller than container
      });
    }

    setupScrollSpeed() {
      if (!this.track) return;
      
      const speedSetting = this.track.dataset.speed;
      const scrollSpeed = speedSetting ? `${speedSetting}s` : '30s';
      
      // Set CSS custom property for animation duration
      this.track.style.setProperty('--scroll-speed', scrollSpeed);
    }

    setupPauseOnHover() {
      if (!this.track) return;
      
      // Hover pause functionality removed - animation runs continuously
    }

    // Method to update scroll speed (useful for theme editor)
    updateScrollSpeed(newSpeed) {
      if (!this.track) return;
      
      const scrollSpeed = `${newSpeed}s`;
      this.track.style.setProperty('--scroll-speed', scrollSpeed);
      this.track.dataset.speed = newSpeed;
    }

    // Cleanup
    disconnectedCallback() {
      if (this.track) {
        this.track.removeEventListener('mouseenter', this.pauseAnimation);
        this.track.removeEventListener('mouseleave', this.resumeAnimation);
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        clearTimeout(this._resizeTimeout);
      }
    }
  }

  // Export for potential manual usage
  window.LogoMarqueeSection = LogoMarqueeSection;
}

// Register the component only if not already registered
if (!customElements.get('logo-marquee-section')) {
  customElements.define('logo-marquee-section', window.LogoMarqueeSection);
}

// Initialize any existing logo marquee sections on page load
document.addEventListener('DOMContentLoaded', () => {
  const existingSections = document.querySelectorAll('logo-marquee-section');
  existingSections.forEach(section => {
    if (!section.initialized) {
      section.initialized = true;
    }
  });
});